<template>
  <teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex flex-col"
      style="background: rgba(0,0,0,0.85);"
    >
      <div
        class="flex items-center justify-between px-4 py-3"
        style="background: var(--color-surface); border-bottom: 1px solid var(--color-border);"
      >
        <span class="font-semibold text-sm" style="color: var(--color-text);">{{ title ?? $t('ui.documentPreview') }}</span>
        <div class="flex items-center gap-2">
          <a
            v-if="blobUrl"
            :href="blobUrl"
            :download="downloadName"
            class="btn-icon"
            :title="$t('common.download')"
          >
            <MdiDownload class="w-[18px] h-[18px]" />
          </a>
          <button
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-transparent border-none cursor-pointer hover:bg-gray-700 text-gray-400 hover:text-gray-100 transition-colors"
            @click="$emit('close')"
            :title="$t('common.close')"
          >
            <MdiClose class="w-4 h-4" />
          </button>
        </div>
      </div>
      <div class="flex-1 flex items-center justify-center">
        <div v-if="loading" class="text-sm" style="color: var(--color-muted);">{{ $t('common.loadingPdf') }}</div>
        <div v-else-if="error" class="text-sm" style="color: #f87171;">{{ error }}</div>
        <iframe
          v-else-if="blobUrl"
          :src="blobUrl"
          class="w-full h-full"
          style="border: none;"
        />
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useAuthStore } from '@/stores/auth.js'
import MdiDownload from '~icons/mdi/download'
import MdiClose from '~icons/mdi/close'

const props = defineProps({
  show:         { type: Boolean, default: false },
  // Either pass a full URL directly (authenticated fetch will be used)
  url:          { type: String, default: null },
  title:        { type: String, default: null },
  downloadName: { type: String, default: 'document.pdf' },
})

defineEmits(['close'])

const blobUrl = ref(null)
const loading = ref(false)
const error   = ref(null)

async function fetchPdf(url) {
  loading.value = true
  error.value   = null
  if (blobUrl.value) { URL.revokeObjectURL(blobUrl.value); blobUrl.value = null }
  try {
    const { baseURL } = await import('@/api/apiClient.js')
    const authStore = useAuthStore()
    const headers = new Headers()
    if (authStore.accessToken) headers.append('Authorization', `Bearer ${authStore.accessToken}`)
    if (authStore.currentTenant?.id != null) headers.append('X-Tenant-ID', String(authStore.currentTenant.id))
    // URL may be absolute (from download_url) or relative path
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`
    const res = await fetch(fullUrl, { headers })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    blobUrl.value = URL.createObjectURL(blob)
  } catch (e) {
    error.value = t('ui.pdfLoadFailed', { error: e.message })
  } finally {
    loading.value = false
  }
}

watch(
  () => props.show,
  (val) => {
    if (val && props.url) {
      fetchPdf(props.url)
    } else if (!val && blobUrl.value) {
      URL.revokeObjectURL(blobUrl.value)
      blobUrl.value = null
      error.value   = null
    }
  }
)
</script>
